/**
 * DAILY CLOSING SERVICE
 * 
 * Verwaltet den Tagesabschluss-Prozess:
 * - Validierung vor Abschluss
 * - Durchführung des Abschlusses
 * - Speicherung von Abschlüssen
 * - Laden historischer Abschlüsse
 */

import { getCurrentBusinessId } from '../utils/sharedCore';
import { kvGet, kvSet, kvSetPersisted } from '../infrastructure/storage/kvAccess';
import balanceSheetService from './balanceSheetService';
import sessionRecorder from '../utils/sessionRecorder';
import { getPaymentBucket } from '../domain/finance/sessionCashflow';
import { resolveBilanLiquidBalances } from '../domain/finance/cashflow';
import { resolveSaleRegisterIncome } from '../domain/sales/checkout';
import { sumSalePriceAdjustments } from '../utils/resolveCheckoutLinePurchase';
import { computeDayFinancialSummary } from '../domain/finance/computeDayFinancialSummary';
import { summarizeRevenue } from '../domain/finance/revenue';
import {
  collectCustomerDebtPayments,
  filterCustomerDebtPaymentsInRange
} from '../domain/customers/balance';
import {
  resolveExpenseTransactionInstant,
  resolveSaleTransactionInstant
} from '../utils/transactionDateTime';

/** Local calendar day bounds (consistent with POS sale dates). */
export function getLocalDayBounds(referenceDate) {
  const d = referenceDate instanceof Date ? new Date(referenceDate) : new Date(referenceDate);
  const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayStart);
  dayEnd.setHours(23, 59, 59, 999);
  return { dayStart, dayEnd };
}

export function toLocalDateString(referenceDate) {
  const d = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isWithinLocalDay(entryDate, dayStart, dayEnd) {
  if (!entryDate) return false;
  const parsed = new Date(entryDate);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed >= dayStart && parsed <= dayEnd;
}

function entryMatchesClosingDay(entry, dayStart, dayEnd) {
  if (!entry) return false;
  const candidates = [entry.date, entry.createdAt, entry.paymentDate, entry.timestamp, entry.saleDate];
  return candidates.some((candidate) => isWithinLocalDay(candidate, dayStart, dayEnd));
}

function saleMatchesClosingDay(sale, dayStart, dayEnd) {
  const instant = resolveSaleTransactionInstant(sale);
  if (instant && !Number.isNaN(instant.getTime())) {
    return instant >= dayStart && instant <= dayEnd;
  }
  return entryMatchesClosingDay(sale, dayStart, dayEnd);
}

function expenseMatchesClosingDay(expense, dayStart, dayEnd) {
  const instant = resolveExpenseTransactionInstant(expense);
  if (instant && !Number.isNaN(instant.getTime())) {
    return instant >= dayStart && instant <= dayEnd;
  }
  return entryMatchesClosingDay(expense, dayStart, dayEnd);
}

class DailyClosingService {
  constructor() {
    this.storageKeyPrefix = 'dailyClosing_';
  }

  normalizePaymentCategory(entry = {}) {
    return getPaymentBucket(entry);
  }

  /**
   * Get storage key for business
   */
  getStorageKey(businessId) {
    return `${this.storageKeyPrefix}${businessId || getCurrentBusinessId()}`;
  }

  /** YYYY-MM-DD key for comparing closing dates regardless of time suffix. */
  normalizeClosingDateKey(date) {
    if (!date) return '';
    const match = String(date).trim().match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return String(date).trim();
    return toLocalDateString(parsed);
  }

  /**
   * Validiert ob Tagesabschluss durchgeführt werden kann (für manuelle Abschlüsse)
   * @param {Object} data - Alle benötigten Daten
   * @param {boolean} isAutomatic - Ob es ein automatischer Abschluss ist (weniger streng)
   * @returns {Object} { valid: boolean, warnings: [], errors: [] }
   */
    validateClosing(data = {}, isAutomatic = false) {
    const {
      sales = [],
      purchases = [],
      expenses = [],
      supermarketPayments = [],
      invoicePayments = [],
      cashRegister = null,
      sessions = [],
      ecarts = [],
      discrepancyManagement = { selfDeviations: [], handoverDeviations: [] }
    } = data;

    const validation = {
      valid: true,
      warnings: [],
      errors: [],
      details: {}
    };

    const validationDate = data.closingDate ? new Date(data.closingDate) : new Date();
    const { dayStart: validationStart, dayEnd: validationEnd } = getLocalDayBounds(validationDate);

    // 1. Prüfe: Alle Worker-Sessions geschlossen?
    const todaySessions = sessions.filter(session => {
      if (!session.loginTime) return false;
      const sessionDate = new Date(session.loginTime);
      const sessionDay = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), sessionDate.getDate());
      return sessionDay.getTime() === validationStart.getTime();
    });

    const activeSessions = todaySessions.filter(s => s.isActive || !s.logoutTime);
    if (activeSessions.length > 0) {
      validation.warnings.push({
        type: 'active_sessions',
        message: {
          ar: `يوجد ${activeSessions.length} جلسة نشطة لم يتم إغلاقها`,
          fr: `${activeSessions.length} session(s) active(s) non fermée(s)`,
          en: `${activeSessions.length} active session(s) not closed`
        },
        count: activeSessions.length,
        sessions: activeSessions.map(s => ({
          workerId: s.workerId,
          workerName: s.workerName,
          loginTime: s.loginTime
        }))
      });
      // Warnung — manueller Abschluss bleibt möglich (Bestätigung im UI)
    }
    validation.details.activeSessionsCount = activeSessions.length;
    validation.details.totalSessionsCount = todaySessions.length;

    // 2. Prüfe: Alle Ecarts bearbeitet?
    const allEcarts = [
      ...(discrepancyManagement.selfDeviations || []),
      ...(discrepancyManagement.handoverDeviations || [])
    ];

    const todayEcarts = allEcarts.filter(ecart => {
      if (!ecart.timestamp) return false;
      const ecartDate = new Date(ecart.timestamp);
      const ecartDay = new Date(ecartDate.getFullYear(), ecartDate.getMonth(), ecartDate.getDate());
      return ecartDay.getTime() === validationStart.getTime();
    });

    const openEcarts = todayEcarts.filter(ecart => 
      ecart.status === 'open' || ecart.status === 'pending'
    );

    if (openEcarts.length > 0) {
      validation.warnings.push({
        type: 'open_ecarts',
        message: {
          ar: `يوجد ${openEcarts.length} خطأ مفتوح لم يتم حله`,
          fr: `${openEcarts.length} écart(s) ouvert(s) non résolu(s)`,
          en: `${openEcarts.length} open écart(s) not resolved`
        },
        count: openEcarts.length,
        ecarts: openEcarts.map(e => ({
          id: e.id,
          workerId: e.workerId || e.previousWorkerId,
          difference: e.deviation?.difference || 0
        }))
      });
      // Warnung, aber nicht blockierend
    }
    validation.details.openEcartsCount = openEcarts.length;
    validation.details.totalEcartsCount = todayEcarts.length;

    // 3. Prüfe: Kassenbestand korrekt?
    // Berechne erwarteten Kassenbestand
    const todaySales = sales.filter((sale) => entryMatchesClosingDay(sale, validationStart, validationEnd));

    const todayPurchases = purchases.filter((purchase) =>
      entryMatchesClosingDay(purchase, validationStart, validationEnd)
    );

    const todayExpenses = expenses.filter((expense) =>
      entryMatchesClosingDay(expense, validationStart, validationEnd)
    );

    const todaySupplierPayments = (Array.isArray(supermarketPayments) ? supermarketPayments : []).filter(
      (payment) => {
        if (!payment || payment.transactionType === 'expense_payment') return false;
        return entryMatchesClosingDay(payment, validationStart, validationEnd);
      }
    );

    const todayInvoicePayments = (Array.isArray(invoicePayments) ? invoicePayments : []).filter(
      (payment) => entryMatchesClosingDay(payment, validationStart, validationEnd)
    );

    const todayDebtPayments = filterCustomerDebtPaymentsInRange(
      collectCustomerDebtPayments(data.customerDebts || []),
      validationStart,
      validationEnd
    );

    const dayFinance = computeDayFinancialSummary({
      sales: todaySales,
      purchases: todayPurchases,
      expenses: todayExpenses,
      supermarketPayments: todaySupplierPayments,
      invoicePayments: todayInvoicePayments,
      // Full sales — same as write path; day-only sales mis-detect setup phase.
      allSales: sales || [],
      customerDebtPayments: todayDebtPayments
    });

    let actualCash = 0;
    if (cashRegister) {
      if (typeof cashRegister === 'object' && cashRegister.balance !== undefined) {
        actualCash = Number(cashRegister.balance) || 0;
      } else if (Array.isArray(cashRegister)) {
        const activeSession = cashRegister.find(s => s.isActive);
        actualCash = activeSession ? (Number(activeSession.currentBalance) || 0) : 0;
      } else {
        actualCash = Number(cashRegister) || 0;
      }
    }

    const firstSession = todaySessions.sort((a, b) =>
      new Date(a.loginTime) - new Date(b.loginTime)
    )[0];

    const initialCash = firstSession ? (Number(firstSession.initialAmount || firstSession.cashAmount || 0)) : 0;

    const expectedCash =
      initialCash +
      dayFinance.paymentTotals.incoming.cash -
      dayFinance.paymentTotals.outgoing.cash;
    const cashDifference = Math.abs(actualCash - expectedCash);

    if (cashDifference > 0.01) {
      validation.warnings.push({
        type: 'cash_mismatch',
        message: {
          ar: `عدم تطابق في النقد: المتوقع ${expectedCash.toFixed(2)}، الفعلي ${actualCash.toFixed(2)}`,
          fr: `Écart de trésorerie: Attendu ${expectedCash.toFixed(2)}, Réel ${actualCash.toFixed(2)}`,
          en: `Cash mismatch: Expected ${expectedCash.toFixed(2)}, Actual ${actualCash.toFixed(2)}`
        },
        expected: expectedCash,
        actual: actualCash,
        difference: cashDifference
      });
      // Warnung, aber nicht blockierend
    }
    validation.details.cashExpected = expectedCash;
    validation.details.cashActual = actualCash;
    validation.details.cashDifference = cashDifference;

    // 4. Prüfe: Gibt es ungespeicherte Daten?
    // (Kann später erweitert werden)

    return validation;
  }

  /**
   * Führt den Tagesabschluss durch
   * @param {Date} date - Datum des Abschlusses (optional, default: heute)
   * @param {string} closedBy - ID des Managers/Users der abschließt
   * @param {Object} data - Alle benötigten Daten
   * @returns {Object} { success: boolean, closing: {}, error: string }
   */
  async performClosing(date = null, closedBy = null, data = {}, options = {}) {
    try {
      const businessId = data.businessId || getCurrentBusinessId();
      if (!businessId) {
        return {
          success: false,
          error: {
            ar: 'معرّف النشاط التجاري غير متوفر',
            fr: 'Identifiant commerce introuvable',
            en: 'Business ID not found'
          }
        };
      }

      const closingDate = date || new Date();
      const effectiveClosingDate = data.closingDate ? new Date(data.closingDate) : closingDate;
      const closingDateStr = toLocalDateString(effectiveClosingDate);
      const closingPayload = { ...data, closingDate: effectiveClosingDate };

      // Prüfe ob für dieses Datum bereits ein Abschluss existiert
      const existingClosing = this.getClosingByDate(closingDateStr, businessId);
      if (existingClosing && existingClosing.status === 'closed') {
        return {
          success: false,
          error: {
            ar: 'تم إغلاق هذا اليوم بالفعل',
            fr: 'Ce jour est déjà fermé',
            en: 'This day is already closed'
          }
        };
      }

      // Bestimme ob es ein automatischer Abschluss ist
      const isAutomatic = closedBy === 'system' || closedBy === 'automatic';
      
      // Validiere vor Abschluss (weniger streng bei automatischen Abschlüssen)
      const validation = this.validateClosing(closingPayload, isAutomatic);

      if (validation.errors.length > 0) {
        return {
          success: false,
          error: {
            ar: 'لا يمكن إغلاق اليوم بسبب أخطاء حرجة',
            fr: 'Impossible de fermer le jour en raison d\'erreurs critiques',
            en: 'Cannot close day due to critical errors'
          },
          validation
        };
      }

      const { dayStart: todayStart, dayEnd: todayEnd } = getLocalDayBounds(effectiveClosingDate);

      const todaySales = (data.sales || []).filter((sale) =>
        saleMatchesClosingDay(sale, todayStart, todayEnd)
      );
      const todayPurchases = (data.purchases || []).filter((purchase) =>
        entryMatchesClosingDay(purchase, todayStart, todayEnd)
      );
      const todayExpenses = (data.expenses || []).filter((expense) =>
        expenseMatchesClosingDay(expense, todayStart, todayEnd)
      );

      const priceAdjustments = todaySales.reduce(
        (sum, sale) => sum + sumSalePriceAdjustments(sale.products),
        0
      );
      const manualPriceLineCount = todaySales.reduce((sum, sale) => {
        const lines = Array.isArray(sale?.products) ? sale.products : [];
        return sum + lines.filter((line) => line?.priceManuallySet).length;
      }, 0);

      const todaySupplierPayments = (data.supermarketPayments || []).filter((payment) => {
        if (!payment || payment.transactionType === 'expense_payment') return false;
        return entryMatchesClosingDay(payment, todayStart, todayEnd);
      });

      const todayInvoicePayments = (data.invoicePayments || []).filter((payment) =>
        entryMatchesClosingDay(payment, todayStart, todayEnd)
      );

      const todayDebtPayments = filterCustomerDebtPaymentsInRange(
        collectCustomerDebtPayments(data.customerDebts || []),
        todayStart,
        todayEnd
      );

      const dayFinance = computeDayFinancialSummary({
        sales: todaySales,
        purchases: todayPurchases,
        expenses: todayExpenses,
        supermarketPayments: todaySupplierPayments,
        invoicePayments: todayInvoicePayments,
        supermarketInvoices: data.supermarketInvoices || [],
        allSales: data.sales || [],
        customerDebtPayments: todayDebtPayments
      });

      const {
        revenue,
        purchasesTotal,
        expensesTotal,
        paymentsTotal,
        paymentsCount,
        outgoing,
        net,
        netCash,
        paymentTotals,
        netLiquid,
        outgoingRegister
      } = dayFinance;

      const expensesRegister = Number(dayFinance.liquid?.total?.outgoingExpenses || 0);
      const paymentsByMethod = dayFinance.paymentsByMethod || [];

      const existingClosings = this.getAllClosings(businessId)
        .slice()
        .sort((a, b) =>
          String(this.normalizeClosingDateKey(b.date)).localeCompare(
            String(this.normalizeClosingDateKey(a.date))
          )
        );
      const previousClosing = existingClosings.find(
        (c) => this.normalizeClosingDateKey(c.date) < closingDateStr
      );
      // Sessions
      const todaySessions = (data.sessions || []).filter(session => {
        if (!session.loginTime) return false;
        const sessionDate = new Date(session.loginTime);
        const sessionDay = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), sessionDate.getDate());
        return sessionDay.getTime() === todayStart.getTime();
      });

      // Ecarts
      const allEcarts = [
        ...(data.discrepancyManagement?.selfDeviations || []),
        ...(data.discrepancyManagement?.handoverDeviations || [])
      ];

      const todayEcarts = allEcarts.filter(ecart => {
        if (!ecart.timestamp) return false;
        const ecartDate = new Date(ecart.timestamp);
        const ecartDay = new Date(ecartDate.getFullYear(), ecartDate.getMonth(), ecartDate.getDate());
        return ecartDay.getTime() === todayStart.getTime();
      });

      // Paiements du jour pour bilan et historique (ventes + dépenses + fournisseurs)
      let payments = [];
      let paymentsError = null;
      try {
        const salesPayments = todaySales.map((sale) => {
          const income = resolveSaleRegisterIncome(sale);
          return {
            id: sale.id,
            amount: income.amount,
            paymentMethod:
              income.channel === 'credit'
                ? 'credit'
                : sale.paymentMethod || income.channel || 'cash',
            registerChannel: income.channel,
            transactionType: 'sale_payment',
            referenceId: sale.id,
            paymentDate: sale.date
          };
        });

        const expensePayments = todayExpenses.map((expense) => ({
          id: expense.id,
          amount: Number(expense.amount || 0),
          paymentMethod: expense.paymentMethod || 'cash',
          transactionType: 'expense_payment',
          referenceId: expense.id,
          paymentDate: expense.date
        }));

        const supplierPaymentsForSheet = [...todaySupplierPayments, ...todayInvoicePayments].map(
          (payment) => ({
            ...payment,
            amount: Number(payment.amount || payment.totalAmount || payment.total || 0),
            paymentDate:
              payment.paymentDate || payment.createdAt || payment.timestamp || payment.date
          })
        );

        payments = [...salesPayments, ...expensePayments, ...supplierPaymentsForSheet];
      } catch (error) {
        paymentsError = error?.message || String(error);
        console.error('Error getting payments:', error);
      }
      
      // Berechne Bilanz
      let balanceSheet = null;
      let liquid = null;
      let balanceSheetError = null;
      try {
        liquid = resolveBilanLiquidBalances({
          sales: data.sales || [],
          expenses: data.expenses || [],
          supermarketPayments: data.supermarketPayments || [],
          invoicePayments: data.invoicePayments || [],
          supermarketInvoices: data.supermarketInvoices || [],
          customerDebts: data.customerDebts || []
        });

        balanceSheet = balanceSheetService.calculateBalanceSheet({
          sales: data.sales || [],
          purchases: data.purchases || [],
          expenses: data.expenses || [],
          payments: [],
          cashRegister: { balance: liquid.cash },
          electronicBalance: liquid.bank,
          supplierDebts: data.supplierDebts || [],
          customerDebts: data.customerDebts || [],
          inventoryValue: data.inventoryValue ?? null,
          products: data.products || [],
          lots: data.lots || [],
          stockSnapshots: data.stockSnapshots ?? null,
          bankAccounts: data.bankAccounts || [],
          supermarketInvoices: data.supermarketInvoices || [],
          discrepancyManagement: data.discrepancyManagement || {
            selfDeviations: [],
            handoverDeviations: []
          },
          expiredProducts: data.expiredProducts || [],
          openingCapital: data.businessInfo?.openingCapital || data.openingCapital || null,
          fixedAssets: data.businessInfo?.fixedAssets || data.fixedAssets || [],
          supermarketPayments: data.supermarketPayments || [],
          invoicePayments: data.invoicePayments || []
        });
      } catch (error) {
        balanceSheetError = error?.message || String(error);
        console.error('Error calculating balance sheet:', error);
      }

      // Erstelle Abschluss-Objekt
      const closing = {
        id: `closing_${closingDateStr}_${businessId}`,
        date: closingDateStr,
        closingTime: new Date().toISOString(),
        closedBy: closedBy || 'unknown',
        status: 'closed',
        summary: {
          revenue,
          purchases: purchasesTotal,
          payments: paymentsTotal,
          /** Affichage SSOT = portion caisse (identique Tableau de Bord) */
          expenses: expensesRegister,
          expensesRegister,
          expensesAccrual: expensesTotal,
          outgoing,
          net,
          netCash,
          netLiquid,
          outgoingRegister,
          paymentTotals,
          salesCount: summarizeRevenue(todaySales).salesCount,
          purchasesCount: todayPurchases.length,
          paymentsCount,
          paymentsByMethod,
          externalFinancing: paymentTotals.externalFinancing || 0,
          externalFinancingByMethod: Object.entries(dayFinance.liquid?.methods || {})
            .filter(([, channel]) => Number(channel?.externalFinancing) > 0)
            .map(([methodKey, channel]) => ({
              methodKey,
              total: Number(channel.externalFinancing) || 0
            }))
            .sort((a, b) => b.total - a.total),
          expensesCount: todayExpenses.length,
          priceAdjustments: Math.round(priceAdjustments * 100) / 100,
          manualPriceLineCount,
          previousClosingDate: previousClosing
            ? this.normalizeClosingDateKey(previousClosing.date)
            : null,
          previousClosingTime:
            previousClosing?.closingTime || previousClosing?.metadata?.createdAt || null
        },
        previousClosing: previousClosing
          ? {
              date: this.normalizeClosingDateKey(previousClosing.date),
              closingTime:
                previousClosing.closingTime || previousClosing.metadata?.createdAt || null
            }
          : null,
        sessions: {
          total: todaySessions.length,
          active: todaySessions.filter(s => s.isActive || !s.logoutTime).length,
          closed: todaySessions.filter(s => !s.isActive && s.logoutTime).length,
          list: todaySessions.map(s => ({
            sessionId: s.sessionId,
            workerId: s.workerId,
            workerName: s.workerName,
            loginTime: s.loginTime,
            logoutTime: s.logoutTime,
            initialAmount: s.initialAmount || s.cashAmount,
            finalAmount: s.finalAmount || s.finalCashAmount || s.cashAmount
          }))
        },
        ecarts: {
          total: todayEcarts.length,
          open: todayEcarts.filter(e => e.status === 'open' || e.status === 'pending').length,
          resolved: todayEcarts.filter(e => e.status === 'accepted' || e.status === 'rejected').length,
          positiveSum: 0,
          negativeSum: 0,
          totalSum: 0
        },
        balanceSheet: balanceSheet || null, // Speichere vollständige Bilanz
        closingSummary: {
          sales: todaySales,
          purchases: todayPurchases,
          expenses: todayExpenses,
          payments: [...todaySupplierPayments, ...todayInvoicePayments],
          supplierPayments: todaySupplierPayments,
          invoicePayments: todayInvoicePayments,
          dayLiquid: {
            cash: dayFinance.liquid.cash.balance,
            electronic: dayFinance.liquid.electronic.balance,
            total: dayFinance.netLiquid
          },
          cashRegister: { balance: liquid?.cash ?? data.cashRegister?.balance ?? 0 },
          electronicBalance: liquid?.bank ?? (data.electronicBalance || 0),
          supplierDebts: data.supplierDebts || [],
          customerDebts: data.customerDebts || [],
          inventoryValue: data.inventoryValue || 0,
          bankAccounts: data.bankAccounts || [],
          supermarketInvoices: data.supermarketInvoices || []
        },
        validation: validation,
        metadata: {
          businessId,
          createdAt: new Date().toISOString(),
          version: '1.1',
          ...(paymentsError ? { paymentsError } : {}),
          ...(balanceSheetError ? { balanceSheetError } : {}),
          partial: Boolean(paymentsError || balanceSheetError)
        }
      };

      if (balanceSheetError || paymentsError) {
        console.warn('[DAILY CLOSING] Closing saved with partial computation:', {
          date: closingDateStr,
          balanceSheetError,
          paymentsError
        });
      }

      // Berechne Ecart-Summen - NUR für akzeptierte oder abgelehnte Ecarts
      // Ecarts mit Status 'pending' oder 'open' werden NICHT in Rechnungen berücksichtigt
      const resolvedTodayEcarts = todayEcarts.filter(ecart => {
        const status = ecart.status || ecart.managerReview?.decision || 'pending';
        return status === 'accepted' || status === 'rejected';
      });
      
      resolvedTodayEcarts.forEach(ecart => {
        const difference = ecart.deviation?.difference || 0;
        if (Number(difference) > 0) {
          closing.ecarts.positiveSum += Math.abs(Number(difference));
        } else if (Number(difference) < 0) {
          closing.ecarts.negativeSum += Math.abs(Number(difference));
        }
      });
      closing.ecarts.totalSum = closing.ecarts.positiveSum - closing.ecarts.negativeSum;

      const persistImmediately =
        options.persistImmediately ??
        (closedBy === 'system' || closedBy === 'automatic');

      const saved = await this.saveClosing(closing, businessId, { persisted: persistImmediately });
      if (!saved) {
        return {
          success: false,
          error: {
            ar: 'فشل حفظ الإغلاق',
            fr: 'Échec de l\'enregistrement de la clôture',
            en: 'Failed to save closing'
          },
          validation
        };
      }

      return {
        success: true,
        closing,
        validation
      };

    } catch (error) {
      console.error('Error performing closing:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Index transaction dates → local YYYY-MM-DD for O(1) activity checks.
   * @param {Array} entries
   * @param {(entry: object) => Date|null} [resolveInstant]
   */
  buildActivityDayIndex(entries, resolveInstant = null) {
    const index = new Map();
    for (const entry of entries || []) {
      if (!entry) continue;
      let instant = null;
      if (typeof resolveInstant === 'function') {
        instant = resolveInstant(entry);
      }
      if (!instant || Number.isNaN(instant?.getTime?.())) {
        const raw = entry.date || entry.createdAt || entry.timestamp || entry.paymentDate;
        if (!raw) continue;
        instant = new Date(raw);
      }
      if (!instant || Number.isNaN(instant.getTime())) continue;
      const key = toLocalDateString(instant);
      const bucket = index.get(key);
      if (bucket) bucket.push(entry);
      else index.set(key, [entry]);
    }
    return index;
  }

  /**
   * Prüft und führt automatische Abschlüsse für vergangene Tage durch
   * Wird um lokale 0 Uhr, beim App-Start (Nachholen) oder beim Beenden ausgeführt
   * @param {Object} data - Alle benötigten Daten (sales, purchases, expenses, etc.)
   * @param {string} closedBy - ID des Users/Systems der abschließt
   * @param {{ lookbackDays?: number }} [options]
   * @returns {Object} { processed: [], errors: [], skippedBeyondWindow: [] }
   */
  async checkAndPerformAutomaticClosings(data = {}, closedBy = 'system', options = {}) {
    const businessId = getCurrentBusinessId();
    if (!businessId) {
      return { processed: [], errors: ['No business ID found'], skippedBeyondWindow: [] };
    }

    const lookbackDays = Math.max(1, Number(options.lookbackDays) || 31);
    const processed = [];
    const errors = [];
    const skippedBeyondWindow = [];
    const now = new Date();

    // One KV read + Set for O(1) closed-day checks across the window
    const closedDates = new Set(
      this.getAllClosings(businessId)
        .filter((c) => c?.status === 'closed')
        .map((c) => this.normalizeClosingDateKey(c.date))
        .filter(Boolean)
    );

    const salesByDay = this.buildActivityDayIndex(data.sales, (sale) =>
      resolveSaleTransactionInstant(sale)
    );
    const purchasesByDay = this.buildActivityDayIndex(data.purchases);
    const expensesByDay = this.buildActivityDayIndex(data.expenses, (expense) =>
      resolveExpenseTransactionInstant(expense)
    );

    const windowStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() - lookbackDays,
      0,
      0,
      0,
      0
    );

    // Surface older unclosed activity so operators know catch-up did not cover it
    for (const [dateStr, daySales] of salesByDay) {
      if (closedDates.has(dateStr)) continue;
      const d = new Date(`${dateStr}T12:00:00`);
      if (Number.isNaN(d.getTime()) || d >= windowStart) continue;
      const hasActivity =
        (daySales?.length || 0) > 0 ||
        (purchasesByDay.get(dateStr)?.length || 0) > 0 ||
        (expensesByDay.get(dateStr)?.length || 0) > 0;
      if (hasActivity) {
        skippedBeyondWindow.push(dateStr);
      }
    }
    if (skippedBeyondWindow.length > 0) {
      console.warn(
        `[DAILY CLOSING] ${skippedBeyondWindow.length} Tag(e) mit Aktivität älter als ${lookbackDays} Tage — nicht nachgeholt:`,
        skippedBeyondWindow.slice(0, 10)
      );
    }

    for (let daysAgo = 1; daysAgo <= lookbackDays; daysAgo++) {
      const checkDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysAgo, 12, 0, 0, 0);
      const dateStr = toLocalDateString(checkDate);

      if (closedDates.has(dateStr)) {
        continue;
      }

      const daySales = salesByDay.get(dateStr) || [];
      const dayPurchases = purchasesByDay.get(dateStr) || [];
      const dayExpenses = expensesByDay.get(dateStr) || [];

      if (daySales.length === 0 && dayPurchases.length === 0 && dayExpenses.length === 0) {
        continue;
      }

      try {
        const closingData = {
          sales: data.sales || [],
          purchases: data.purchases || [],
          expenses: data.expenses || [],
          supermarketPayments: data.supermarketPayments || [],
          invoicePayments: data.invoicePayments || [],
          cashRegister: data.cashRegister || null,
          sessions: data.sessions || [],
          ecarts: data.ecarts || [],
          discrepancyManagement: data.discrepancyManagement || {},
          supplierDebts: data.supplierDebts || [],
          customerDebts: data.customerDebts || [],
          inventoryValue: data.inventoryValue ?? null,
          products: data.products || [],
          lots: data.lots || [],
          expiredProducts: data.expiredProducts || [],
          supermarketInvoices: data.supermarketInvoices || [],
          bankAccounts: data.bankAccounts || [],
          closingDate: checkDate,
          businessId
        };

        const result = await this.performClosing(checkDate, closedBy, closingData, {
          persistImmediately: true
        });

        if (result.success) {
          closedDates.add(dateStr);
          processed.push({
            date: dateStr,
            closing: result.closing,
            partial: result.closing?.metadata?.partial === true
          });
        } else {
          errors.push({
            date: dateStr,
            error: result.error
          });
        }
      } catch (error) {
        errors.push({
          date: dateStr,
          error: error.message
        });
      }
    }

    return { processed, errors, skippedBeyondWindow, lookbackDays };
  }

  /**
   * Speichert einen Tagesabschluss
   * @param {{ persisted?: boolean }} [options] — persisted: await IndexedDB/localStorage mirror
   */
  async saveClosing(closing, businessId = null, { persisted = false } = {}) {
    try {
      const storageKey = this.getStorageKey(businessId);
      const existingClosings = this.getAllClosings(businessId);
      
      // Entferne alten Abschluss für dasselbe Datum falls vorhanden
      const closingDateKey = this.normalizeClosingDateKey(closing.date);
      const filteredClosings = existingClosings.filter(
        (c) => this.normalizeClosingDateKey(c.date) !== closingDateKey
      );
      
      // Füge neuen Abschluss hinzu
      filteredClosings.push(closing);
      
      // Sortiere nach Datum (neueste zuerst)
      filteredClosings.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      if (persisted) {
        await kvSetPersisted(storageKey, filteredClosings);
      } else {
        kvSet(storageKey, filteredClosings);
      }

      return true;
    } catch (error) {
      console.error('Error saving closing:', error);
      return false;
    }
  }

  /**
   * Lädt alle Tagesabschlüsse
   */
  getAllClosings(businessId = null) {
    try {
      const storageKey = this.getStorageKey(businessId);
      const stored = kvGet(storageKey);
      if (stored == null) return [];
      if (Array.isArray(stored)) return stored;
      if (typeof stored === 'string') {
        try {
          const p = JSON.parse(stored);
          return Array.isArray(p) ? p : [];
        } catch {
          return [];
        }
      }
      return [];
    } catch (error) {
      console.error('Error loading closings:', error);
      return [];
    }
  }

  /**
   * Lädt Tagesabschluss für ein bestimmtes Datum
   */
  getClosingByDate(date, businessId = null) {
    const closings = this.getAllClosings(businessId);
    const dateKey = this.normalizeClosingDateKey(date);
    // Prefer exact prefix match first (common YYYY-MM-DD storage)
    const exact = closings.find((c) => String(c?.date || '').startsWith(dateKey));
    if (exact && this.normalizeClosingDateKey(exact.date) === dateKey) return exact;
    return closings.find((c) => this.normalizeClosingDateKey(c.date) === dateKey);
  }

  /**
   * Lädt Tagesabschlüsse für einen Zeitraum
   */
  getClosingsByDateRange(startDate, endDate, businessId = null) {
    const closings = this.getAllClosings(businessId);
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    return closings.filter(closing => {
      const closingDate = new Date(closing.date);
      return closingDate >= start && closingDate <= end;
    });
  }

  /**
   * Prüft ob ein Datum bereits abgeschlossen wurde
   */
  isDateClosed(date, businessId = null) {
    const closing = this.getClosingByDate(date, businessId);
    return closing && closing.status === 'closed';
  }

  /**
   * Löscht einen Tagesabschluss (nur für Admin)
   */
  async deleteClosing(date, businessId = null) {
    try {
      const storageKey = this.getStorageKey(businessId);
      const closings = this.getAllClosings(businessId);
      const dateKey = this.normalizeClosingDateKey(date);
      const filtered = closings.filter(
        (c) => this.normalizeClosingDateKey(c.date) !== dateKey
      );

      if (filtered.length === closings.length) {
        return false;
      }

      await kvSetPersisted(storageKey, filtered);
      return true;
    } catch (error) {
      console.error('Error deleting closing:', error);
      return false;
    }
  }

  /**
   * Berechnet Jahresbilanz aus Tagesabschlüssen
   * @param {Number} year - Jahr (optional, default: aktuelles Jahr)
   * @param {String} businessId - Business ID (optional)
   * @param {object} [extras] — openingCapital / fixedAssets (live settings; closings omit them)
   * @returns {Object} Jahresbilanz
   */
  calculateYearlyBalanceSheet(year = null, businessId = null, extras = {}) {
    try {
      const targetYear = year || new Date().getFullYear();
      const allClosings = this.getAllClosings(businessId);
      
      if (allClosings.length === 0) {
        console.warn(`⚠️ [DAILY CLOSING] No closings found for yearly balance sheet calculation`);
        return null;
      }

      return balanceSheetService.calculateYearlyBalanceSheetFromClosings(
        allClosings,
        targetYear,
        extras
      );
    } catch (error) {
      console.error('Error calculating yearly balance sheet:', error);
      return null;
    }
  }

  /**
   * Gibt alle verfügbaren Jahre zurück, für die Abschlüsse existieren
   * @param {String} businessId - Business ID (optional)
   * @returns {Array} Array von Jahren
   */
  getAvailableYears(businessId = null) {
    try {
      const allClosings = this.getAllClosings(businessId);
      const years = new Set();
      
      allClosings.forEach(closing => {
        if (closing.date) {
          const date = new Date(closing.date);
          years.add(date.getFullYear());
        }
      });
      
      return Array.from(years).sort((a, b) => b - a); // Neueste zuerst
    } catch (error) {
      console.error('Error getting available years:', error);
      return [];
    }
  }
}

// Singleton instance
const dailyClosingService = new DailyClosingService();
export default dailyClosingService;

